import React, {
  useEffect,
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import {
  useSandboxHeartbeatMutation,
  getSession,
  listSessions,
  createWorkspace as createWorkspaceApi,
  setWorkspaceTypeHeader,
  getActiveWorkspaceSession,
  useCreateWorkspaceMutation,
  getAgentSessionStatus,
  setDirectWorkspaceConnection,
  getKernel,
  getWorkspaceStatus,
} from "../api/jupyterManagementApi";
import { getConfiguration } from "../api/settingsApi";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

interface JupyterWorkspaceState {
  project: any | null;
  kernelId: string | null;
  sessionId: string | null;
  workspaceUrl: string | null;
  status: "none" | "provisioning" | "active" | "error" | "stopping";
  instanceId: string | null;
  serverId?: string | null;
  createdAt: string | null;
  lastActivity: string | null;
  folderPath: string | null;
  notebookPath: string | null;
  serverReady: boolean;
  kernelExecutionState: string | null;
  errorMessage?: string;
  remainingTime?: number | null;
  totalAllowedTime?: number | null;
  timeUsed?: number | null;
  extension_requested?: boolean;
  isPracticeProject?: boolean;
  workspaceType: "cloud" | "local";
  globalSandboxMode: "cloud" | "local";
}

interface JupyterWorkspaceContextType {
  workspace: JupyterWorkspaceState;
  setWorkspace: (workspace: Partial<JupyterWorkspaceState>) => void;
  updateWorkspace: (updates: Partial<JupyterWorkspaceState>) => void;
  resetWorkspace: () => void;
  isWorkspaceReady: () => boolean;
  updateActivity: () => void;
  clearWorkspaceStorage: () => void;
  validateSession: () => Promise<boolean>;
  isConnectionVerified: boolean;
  setIsPollingActive: (active: boolean) => void;
}
const WORKSPACE_STORAGE_KEY = "jupyterWorkspaceState";
const parsedActiveHeartbeatMs = Number.parseInt(
  import.meta.env.VITE_SANDBOX_ACTIVE_HEARTBEAT_INTERVAL_MS || "15000",
  10,
);
const ACTIVE_HEARTBEAT_INTERVAL_MS =
  Number.isFinite(parsedActiveHeartbeatMs) && parsedActiveHeartbeatMs > 0
    ? Math.max(5000, parsedActiveHeartbeatMs)
    : 15000;
const PROVISIONING_POLL_INTERVAL_MS = 5000;

const initialWorkspaceState: JupyterWorkspaceState = {
  project: null,
  kernelId: null,
  sessionId: null,
  workspaceUrl: null,
  status: "none",
  instanceId: null,
  createdAt: null,
  lastActivity: null,
  folderPath: null,
  notebookPath: null,
  serverReady: false,
  kernelExecutionState: null,
  remainingTime: null,
  totalAllowedTime: null,
  timeUsed: null,
  isPracticeProject: false,
  workspaceType: "cloud",
  globalSandboxMode: "cloud",
};

const loadWorkspaceFromStorage = (): any => {
  try {
    const stored = sessionStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Failed to load workspace from sessionStorage:", error);
  }
  // return a fresh copy to avoid accidental shared-reference mutation
  return { ...initialWorkspaceState };
};

const saveWorkspaceToStorage = (workspace: JupyterWorkspaceState): void => {
  try {
    sessionStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspace));
  } catch (error) {
    console.error("Failed to save workspace to sessionStorage:", error);
  }
};

const clearWorkspaceFromStorage = (): void => {
  try {
    sessionStorage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear workspace from sessionStorage:", error);
  }
};

function isHeartbeatSurfaceRoute(path: string): boolean {
  return (
    path.includes("/notebook/") ||
    path.includes("/grading-sandbox") ||
    path.includes("/student-notebook")
  );
}

/** Parse BITS error envelope from axios (heartbeat → 404 timeout, etc.) */
function parseSandboxHeartbeatHttpError(error: unknown): {
  status?: number;
  code?: string;
  message?: string;
} {
  const err = error as {
    response?: { status?: number; data?: { data?: { message?: string; data?: { code?: string }; code?: string } } };
  };
  const status = err?.response?.status;
  const inner = err?.response?.data?.data;
  const message = typeof inner?.message === "string" ? inner.message : undefined;
  const code =
    typeof inner?.data?.code === "string"
      ? inner.data.code
      : typeof inner?.code === "string"
        ? inner.code
        : undefined;
  return { status, code, message };
}

const JupyterWorkspaceContext =
  createContext<JupyterWorkspaceContextType | null>(null);

export const useJupyterWorkspace = () => {
  const context = useContext(JupyterWorkspaceContext);
  if (!context) {
    throw new Error(
      "useJupyterWorkspace must be used within a JupyterWorkspaceProvider",
    );
  }
  return context;
};

export function JupyterWorkspaceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [workspace, setWorkspaceState] = useState<JupyterWorkspaceState>(
    initialWorkspaceState,
  );
  const [isValidated, setIsValidated] = useState(false);
  const [isConnectionVerified, setIsConnectionVerified] = useState(false);
  const [isPollingActive, setIsPollingActive] = useState(true);
  
  const location = useLocation();
  const pathname = location.pathname;

  const heartbeatMutation = useSandboxHeartbeatMutation();

  // Helper to determine if we are on a Jupyter-relevant route
  const isJupyterRoute = useCallback((path: string) => {
    const jupyterPaths = [
      "/notebook/",
      "/sandbox",
      "/grading-sandbox",
      "/scripts/",
      "/datasets/",
      "/project-details/",
      "/practice-projects/",
      "/student-notebook",
    ];
    return jupyterPaths.some((p) => path.includes(p));
  }, []);

  useEffect(() => {
    const validateSessionOnMount = async () => {
      let storedData = loadWorkspaceFromStorage();

      // 1. Fetch global config (sandbox mode; idle minutes logged for ops / parity with agent)
      let globalMode: "cloud" | "local" | null = null;
      try {
        const config = await getConfiguration();
        globalMode = config?.data?.sandboxConfig?.mode || "local";
        console.log("[WorkspaceContext] Global sandbox mode:", globalMode);

        const idleMin = config?.data?.sandboxConfig?.idleTimeoutMinutes;
        if (typeof idleMin === "number" && Number.isFinite(idleMin) && idleMin > 0) {
          console.log(
            "[WorkspaceContext] Sandbox idle timeout from server (minutes):",
            idleMin,
          );
        }

        // Push the mode to the API headers immediately so the proxy knows how to route.
        setWorkspaceTypeHeader(globalMode === "local" ? "local" : "cloud");
      } catch (err) {
        console.error("Failed to fetch global config:", err);
      }

      if (!storedData) {
        storedData = { ...initialWorkspaceState };
      }

      // 2. Enforce global mode if present
      if (globalMode) {
        storedData.workspaceType = globalMode;
        storedData.globalSandboxMode = globalMode;
      }

      const preferredType = storedData.workspaceType || "cloud";

      // Clear any stale direct connection info from storage on reload
      // to ensure we always resolve the latest URL from the backend.
      if (storedData.project?.id) {
        console.log("[WorkspaceContext] Clearing stale connection info on mount.");
        setDirectWorkspaceConnection(null, storedData.project.id);
        storedData.workspaceUrl = null;
      }

      // If sessionId is empty/missing, try to recover it from the backend cache
      if (!storedData.sessionId && storedData.project?.id) {
        try {
          const active = await getActiveWorkspaceSession(storedData.project.id);
          if (active?.sessionId) {
            storedData = { ...storedData, sessionId: active.sessionId };
          }
        } catch (e) {
          console.warn("Could not recover sessionId from backend:", e);
        }
      }

      // Restore stored structure immediately
      setWorkspaceState(storedData);
      saveWorkspaceToStorage(storedData);

      // 3. Connection Verification: Essential for Local Agent parity
      if (storedData.project?.id) {
        try {
          const agentStatus = await getAgentSessionStatus(storedData.project.id);
          if (agentStatus.status === "CONNECTED" && agentStatus.workspaceUrl) {
            console.log("[WorkspaceContext] Verified direct connection:", agentStatus.workspaceUrl);
            setDirectWorkspaceConnection(agentStatus.workspaceUrl, storedData.project.id);
            setWorkspaceState(prev => ({ ...prev, workspaceUrl: agentStatus.workspaceUrl ?? null }));
          } else {
            console.log("[WorkspaceContext] Agent not directly connected, using proxy.");
            setDirectWorkspaceConnection(null, storedData.project.id);
          }
        } catch (e) {
          console.warn("[WorkspaceContext] Could not verify agent connection on mount:", e);
        }
      }
      
      setIsConnectionVerified(true);

      if (storedData.sessionId) {
        try {
          const result = await heartbeatMutation.mutateAsync({
            sessionId: storedData.sessionId,
            projectId: storedData.project?.id,
          });
          if (result.status === "active") {
            // Always attempt to recover the live kernel ID from the server on mount.
            // This ensures that if the agent/container was restarted, we pick up the new 
            // kernel ID and token instead of using stale ones from sessionStorage.
            let recoveredKernelId: string | null = null;
            if (storedData.project?.id) {
              try {
                const sessions = await listSessions();
                const match = sessions.find((s: any) =>
                  s.path?.includes(storedData.project.id)
                );
                if (match?.kernel?.id) {
                  recoveredKernelId = match.kernel.id;
                  console.log("[WorkspaceContext] Recovered live kernel ID:", recoveredKernelId);
                }
              } catch (_) { /* ignore */ }
            }
            
            // If we didn't find one but storage had one, we still clear it if it was likely stale
            // (Only for local mode where container restarts are frequent).
            const isLocal = storedData.workspaceType === "local";
            if (!recoveredKernelId && isLocal) {
               console.log("[WorkspaceContext] No live kernel found, clearing potentially stale storage ID.");
            }
            setWorkspaceState({
              ...storedData,
              kernelId: recoveredKernelId,
              remainingTime: result.remainingTime,
              totalAllowedTime:
                result.totalAllowedTime || storedData.totalAllowedTime,
              extension_requested: result.extension_requested,
              isPracticeProject: result.isPracticeProject || storedData.isPracticeProject,
              status: "active",
            });


            setIsValidated(true);
          } else {
            clearWorkspaceFromStorage();
            setWorkspaceState(initialWorkspaceState);
          }
        } catch (error) {
          console.error("Session validation failed:", error);
          clearWorkspaceFromStorage();
          setWorkspaceState(initialWorkspaceState);
        }
      } else {
        // No sessionId stored yet — leave state as-is so the background heartbeat
        // loop can attempt recovery via getActiveWorkspaceSession.
        console.log("[WorkspaceContext] No sessionId on mount, allowing background recovery.");
      }
    };
    validateSessionOnMount();
  }, []);

  useEffect(() => {
    saveWorkspaceToStorage(workspace);
  }, [workspace]);

  const setWorkspace = useCallback(
    (newWorkspace: Partial<JupyterWorkspaceState>) => {
      setWorkspaceState((prev) => ({ ...prev, ...newWorkspace }));
    },
    [],
  );

  const updateWorkspace = useCallback(
    (updates: Partial<JupyterWorkspaceState>) => {
      setWorkspaceState((prev) => {
        const next = { ...prev, ...updates };
        saveWorkspaceToStorage(next);
        return next;
      });
    },
    [],
  );

  const resetWorkspace = useCallback(() => {
    clearWorkspaceFromStorage();
    // Reset session-specific state but preserve the core configuration (modes)
    setWorkspaceState((prev) => ({
      ...initialWorkspaceState,
      workspaceType: prev.workspaceType,
      globalSandboxMode: prev.globalSandboxMode,
    }));
    // mark as not validated so the provider won't re-save until a fresh validation occurs
    setIsValidated(false);
  }, []);

  const clearWorkspaceStorage = useCallback(() => {
    // Reset session-specific state but preserve the core configuration (modes)
    setWorkspaceState((prev) => ({
      ...initialWorkspaceState,
      workspaceType: prev.workspaceType,
      globalSandboxMode: prev.globalSandboxMode,
    }));
    setIsValidated(false);
    clearWorkspaceFromStorage();
  }, []);

  const validateSession = useCallback(async (): Promise<boolean> => {
    if (workspace.status === "error" || workspace.status === "stopping") {
      return false;
    }

    let currentSessionId = workspace.sessionId;

    // If sessionId is missing or is a local dummy ID, attempt recovery from the backend
    if ((!currentSessionId || currentSessionId.startsWith('local_')) && workspace.project?.id) {
      try {
        const active = await getActiveWorkspaceSession(workspace.project.id);
        if (active?.sessionId) {
          currentSessionId = active.sessionId;
          updateWorkspace({ sessionId: currentSessionId });
        }
      } catch (e) {
        console.warn("Could not recover sessionId during validation:", e);
      }
    }

    // Skip heartbeats for dummy IDs — they will always fail on the backend.
    // The background polling of workspaceStatus or getActiveWorkspaceSession will eventually
    // replace it with a real UUID once the Agent notifies the backend.
    if (currentSessionId?.startsWith('local_')) {
      console.log("[WorkspaceContext] Skipping heartbeat for local dummy sessionId.");
      return true; // Keep the loop running
    }

    // Attempt to fetch Direct Connection details (Local Agent tunnel or Cloud Agent URL)
    if (workspace.project?.id) {
      try {
        const agentStatus = await getAgentSessionStatus(workspace.project.id);
        const isAgentConnected = agentStatus.status === "CONNECTED";

        if (isAgentConnected && agentStatus.workspaceUrl) {
          setDirectWorkspaceConnection(agentStatus.workspaceUrl, workspace.project.id);
        } else {
          setDirectWorkspaceConnection(null, workspace.project.id); // Clear if not connected directly
        }

        // Optimization: If we are in local mode and the agent is NOT connected, 
        // skip the backend heartbeat call. There's no reachable session to heartbeat.
        if (workspace.workspaceType === "local" && !isAgentConnected) {
          console.log("[WorkspaceContext] Local agent offline. Skipping backend heartbeat.");
          return true; // Keep the loop alive so we can recover when the agent wakes up
        }
      } catch (e) {
        console.warn("Could not verify agent connection details:", e);
      }
    } else {
      setDirectWorkspaceConnection(null, workspace.project?.id);
    }

    if (!currentSessionId) {
      console.warn("[WorkspaceContext] Cannot validate session: sessionId is still missing.");
      return false;
    }
    try {
      const isLocal = workspace.globalSandboxMode === "local" && workspace.workspaceType === "local";
      const result = await heartbeatMutation.mutateAsync({
        sessionId: currentSessionId,
        isLocal,
        projectId: workspace.project?.id,
      });
      if (result.status === "active") {
        updateWorkspace({
          remainingTime: result.remainingTime,
          totalAllowedTime:
            result.totalAllowedTime || workspace.totalAllowedTime,
          extension_requested: result.extension_requested,
          isPracticeProject: result.isPracticeProject || workspace.isPracticeProject,
          status: "active",
        });


        // Final Kernel Guard: If we are active but kernelId is missing (or stale), try to recover it.
        if (workspace.project?.id) {
           let isStale = false;
           if (workspace.kernelId) {
             try {
                await getKernel(workspace.kernelId);
             } catch (e: any) {
                if (e.response?.status === 404) {
                   console.log("[WorkspaceContext] Kernel 404 detected (stale), clearing ID.");
                   isStale = true;
                }
             }
           }

           if (!workspace.kernelId || isStale) {
              try {
                 const sessions = await listSessions();
                 const match = sessions.find((s: any) => s.path?.includes(workspace.project.id));
                 if (match?.kernel?.id) {
                   console.log("[WorkspaceContext] Recovered new kernel ID:", match.kernel.id);
                   updateWorkspace({ kernelId: match.kernel.id });
                 } else if (isStale) {
                   updateWorkspace({ kernelId: null });
                 }
              } catch (_) {}
           }
        }

        return true;
      } else {
        clearWorkspaceStorage();
        return false;
      }
    } catch (error: any) {
      console.error("Session validation failed:", error);

      if (error.response?.status === 404) {
        const { code, message } = parseSandboxHeartbeatHttpError(error);
        const errMsg =
          message ||
          "Your sandbox session is no longer active. Start the workspace again from the project.";
        const projectId = workspace.project?.id;
        if (projectId) {
          setDirectWorkspaceConnection(null, projectId);
        }
        // Do NOT call resetWorkspace() here: NotebookPage syncs URL projectId → context and
        // would immediately set status back to "provisioning" while sessionId is null, leaving
        // the UI stuck on "Initializing..." with a dead iframe.
        updateWorkspace({
          status: "error",
          errorMessage: errMsg,
          sessionId: null,
          kernelId: null,
          workspaceUrl: null,
          serverReady: false,
          remainingTime: null,
          totalAllowedTime: null,
          notebookPath: null,
          folderPath: null,
        });
        if (projectId) {
          toast.dismiss(`sandbox-${projectId}`);
        }
        toast.error(errMsg, { id: "sandbox-session-ended", duration: 14_000 });
        setIsValidated(false);
        const gone = new Error(errMsg);
        (gone as { code?: string }).code =
          code === "SESSION_TIMEOUT"
            ? "SESSION_TIMEOUT"
            : code === "SESSION_ENDED"
              ? "SESSION_ENDED"
              : "SESSION_GONE";
        throw gone;
      }

      setIsValidated(false);
      return false;
    }
  }, [
    workspace?.sessionId,
    workspace?.totalAllowedTime,
    workspace?.status,
    workspace?.project?.id,
  ]);

  const isWorkspaceReady = useCallback(() => {
    return workspace?.status === "active" && workspace.kernelId !== null;
  }, [workspace?.status, workspace?.kernelId]);

  const updateActivity = useCallback(() => {
    setWorkspaceState((prev) => ({
      ...prev,
      lastActivity: new Date().toISOString(),
    }));
  }, []);

  // Background Heartbeat lifecycle
  useEffect(() => {
    // Start heartbeats if we have a session OR if we have a project to recover a session for.
    // Only stop if status is explicitly 'none' or we have no project context at all.
    if (workspace.status === "none" || !workspace.project?.id) {
      return;
    }

    if (workspace.status === "error" || workspace.status === "stopping") {
      return;
    }

    if (!isPollingActive) {
      console.log(
        "[WorkspaceContext] Polling suspended (isPollingActive=false). Status:",
        workspace.status,
      );
      return;
    }

    const broadJupyterRoute = isJupyterRoute(pathname);
    const onHeartbeatSurface = isHeartbeatSurfaceRoute(pathname);

    if (workspace.status === "provisioning") {
      if (!broadJupyterRoute) {
        console.log(
          "[WorkspaceContext] Provisioning poll suspended — not on a Jupyter-related route:",
          pathname,
        );
        return;
      }
    } else {
      if (!onHeartbeatSurface) {
        console.log(
          "[WorkspaceContext] Heartbeat loop suspended — not on notebook / grading / student-notebook surface:",
          pathname,
        );
        return;
      }
    }

    let timeoutId: NodeJS.Timeout;
    let cancelled = false;

    // Background Heartbeat loop with diagnostic logging
    console.log("[WorkspaceContext] Initializing heartbeat loop. Status:", workspace.status, "ProjectID:", workspace.project?.id, "SessionID:", workspace.sessionId);

    const performHeartbeat = async () => {
      if (cancelled) return;
      let scheduleNext = true;

      console.log("[WorkspaceContext] Starting heartbeat check...");
      try {
        if (workspace.status === "provisioning" && workspace.project?.id) {
          const statusData = await getWorkspaceStatus(workspace.project.id);
          if (cancelled) return;
          if (statusData.status === "active" && statusData.data) {
            console.log("[WorkspaceContext] Provisioning complete detected via global polling.");
            const d = statusData.data;
            const server = d.server || {};
            const kernel = d.kernel || {};

            updateWorkspace({
              status: "active",
              instanceId: server.state?.object_name || null,
              serverId: server.name || null,
              kernelId: kernel.id || null,
              sessionId: d.sessionId,
              workspaceUrl: server.url || null,
              createdAt: server.started || null,
              lastActivity: kernel.last_activity || null,
              folderPath: d.folderPath,
              notebookPath: d.notebookPath,
              serverReady: !!server.ready,
              kernelExecutionState: kernel.execution_state || null,
              remainingTime: d.remainingTime,
              totalAllowedTime: d.totalAllowedTime,
            });
            toast.success("Sandbox is ready!", { id: `sandbox-${workspace.project.id}` });
          } else if (statusData.status === "error") {
            updateWorkspace({ status: "error", errorMessage: statusData.message });
            toast.error(statusData.message || "Sandbox provisioning failed", { id: `sandbox-${workspace.project.id}` });
          }
        } else {
          try {
            const success = await validateSession();
            if (cancelled) return;
            console.log(
              "[WorkspaceContext] Heartbeat validation result:",
              success,
            );
          } catch (e: unknown) {
            if (cancelled) return;
            const c = (e as { code?: string })?.code;
            if (
              c === "SESSION_GONE" ||
              c === "SESSION_TIMEOUT" ||
              c === "SESSION_ENDED"
            ) {
              scheduleNext = false;
            } else {
              console.error("[WorkspaceContext] validateSession error:", e);
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[WorkspaceContext] Background heartbeat error:", err);
      } finally {
        if (!cancelled && scheduleNext) {
          timeoutId = setTimeout(
            performHeartbeat,
            workspace.status === "provisioning"
              ? PROVISIONING_POLL_INTERVAL_MS
              : ACTIVE_HEARTBEAT_INTERVAL_MS,
          );
        }
      }
    };

    // If we are active but missing remainingTime (e.g., after navigation probe),
    // trigger a heartbeat immediately to sync the timer.
    const needsImmediateSync = workspace.status === "active" && workspace.remainingTime === null;
    const initialDelay = needsImmediateSync 
      ? 500 
      : (!workspace.sessionId ? 1000 : (workspace.status === "provisioning" ? 2000 : 3000));
    
    timeoutId = setTimeout(performHeartbeat, initialDelay);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    workspace.sessionId,
    workspace.project?.id,
    workspace.status,
    workspace.remainingTime === null,
    isValidated,
    pathname,
    isPollingActive,
  ]);

  const contextValue: JupyterWorkspaceContextType = {
    workspace,
    setWorkspace,
    updateWorkspace,
    resetWorkspace,
    isWorkspaceReady,
    updateActivity,
    clearWorkspaceStorage,
    validateSession,
    isConnectionVerified,
    setIsPollingActive,
  };

  return (
    <JupyterWorkspaceContext.Provider value={contextValue}>
      {children}
    </JupyterWorkspaceContext.Provider>
  );
}
